import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Building2, ChevronDown, ChevronRight, Users, Crown, LayoutGrid, List } from "lucide-react";
import api from "../lib/api";

const OrgChart = () => {
  const { currentBusiness } = useAuth();
  const [orgData, setOrgData] = useState({ nodes: [], departments: [] });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("chart");
  const [expandedNodes, setExpandedNodes] = useState({});

  useEffect(() => {
    if (currentBusiness?.id) fetchOrgChart();
  }, [currentBusiness?.id]);

  const fetchOrgChart = async () => {
    try {
      const res = await api.get(`/org-chart?business_id=${currentBusiness.id}`);
      setOrgData(res.data);
      const expanded = {};
      res.data.nodes.forEach(n => { expanded[n.id] = true; });
      setExpandedNodes(expanded);
    } catch (e) {
      toast.error("Failed to load org chart");
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const buildTree = useCallback((nodes) => {
    const childMap = {};
    const roots = [];
    nodes.forEach(n => { childMap[n.id] = []; });
    nodes.forEach(n => {
      if (n.reports_to && childMap[n.reports_to]) {
        childMap[n.reports_to].push(n);
      } else {
        roots.push(n);
      }
    });
    Object.keys(childMap).forEach(k => {
      childMap[k].sort((a, b) => {
        if (a.is_department_head !== b.is_department_head) return b.is_department_head ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
    });
    roots.sort((a, b) => {
      if (a.is_department_head !== b.is_department_head) return b.is_department_head ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
    return { roots, childMap };
  }, []);

  const { roots, childMap } = buildTree(orgData.nodes);
  const totalNodes = orgData.nodes.length;
  const deptHeads = orgData.nodes.filter(n => n.is_department_head).length;

  const renderChartNode = (node, level) => {
    const children = childMap[node.id] || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedNodes[node.id];

    return (
      <div key={node.id} className="flex flex-col items-center" data-testid={"org-node-" + node.id}>
        <div
          className={"relative px-4 py-3 rounded-xl border-2 shadow-sm min-w-[180px] max-w-[220px] text-center cursor-pointer transition-all hover:shadow-md " +
            (node.is_department_head ? "border-amber-300 bg-gradient-to-b from-amber-50 to-white" : "border-slate-200 bg-white hover:border-primary/40")}
          onClick={() => hasChildren && toggleNode(node.id)}
        >
          {node.is_department_head && (
            <Crown className="h-3.5 w-3.5 text-amber-500 absolute -top-1.5 left-1/2 -translate-x-1/2 bg-white rounded-full" />
          )}
          <p className="font-semibold text-slate-800 text-sm leading-tight">{node.name}</p>
          {node.designation && <p className="text-xs text-slate-500 mt-0.5">{node.designation}</p>}
          {node.department && (
            <Badge variant="outline" className="mt-1.5 text-[10px] bg-primary/5 text-primary border-primary/20">{node.department}</Badge>
          )}
          {hasChildren && (
            <div className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 bg-white border rounded-full p-0.5 text-slate-400">
              {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </div>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div className="flex flex-col items-center">
            <div className="w-px h-5 bg-slate-300"></div>
            <div className="flex gap-6">
              {children.map(child => (
                <div key={child.id} className="flex flex-col items-center">
                  <div className="w-px h-5 bg-slate-300"></div>
                  {renderChartNode(child, level + 1)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTreeItem = (node, level) => {
    const children = childMap[node.id] || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedNodes[node.id];

    return (
      <div key={node.id} style={{ marginLeft: level > 0 ? 24 : 0 }} data-testid={"tree-item-" + node.id}>
        <div
          className={"flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors " + (node.is_department_head ? "bg-amber-50/50" : "")}
          onClick={() => hasChildren && toggleNode(node.id)}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
          ) : (
            <div className="w-4 flex-shrink-0" />
          )}
          {node.is_department_head && <Crown className="h-4 w-4 text-amber-500 flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-800 text-sm">{node.name}</span>
              {node.designation && <span className="text-xs text-slate-400">({node.designation})</span>}
            </div>
          </div>
          {node.department && (
            <Badge variant="outline" className="text-[10px] bg-primary/5 text-primary border-primary/20 flex-shrink-0">{node.department}</Badge>
          )}
          {hasChildren && <span className="text-xs text-slate-400 flex-shrink-0">{children.length} reports</span>}
        </div>
        {hasChildren && isExpanded && (
          <div className="border-l-2 border-slate-200 ml-5">
            {children.map(child => renderTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business first</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="org-chart-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Org Chart</h1>
          <p className="text-slate-500">Organizational hierarchy & reporting structure</p>
        </div>
        <div className="flex gap-2 bg-slate-100 p-1 rounded-full">
          <Button variant={viewMode === "chart" ? "default" : "ghost"} size="sm"
            className={"rounded-full " + (viewMode === "chart" ? "btn-primary" : "")}
            onClick={() => setViewMode("chart")} data-testid="view-chart-btn">
            <LayoutGrid className="h-4 w-4 mr-1" /> Chart
          </Button>
          <Button variant={viewMode === "list" ? "default" : "ghost"} size="sm"
            className={"rounded-full " + (viewMode === "list" ? "btn-primary" : "")}
            onClick={() => setViewMode("list")} data-testid="view-list-btn">
            <List className="h-4 w-4 mr-1" /> List
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10"><Users className="h-6 w-6 text-primary" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{totalNodes}</p><p className="text-sm text-slate-500">Total Employees</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100"><Crown className="h-6 w-6 text-amber-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{deptHeads}</p><p className="text-sm text-slate-500">Department Heads</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100"><Building2 className="h-6 w-6 text-green-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{orgData.departments.length}</p><p className="text-sm text-slate-500">Departments</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="card-soft">
        <CardContent className="p-6">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>
          ) : totalNodes === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p>No employees found. Add employees and set their reporting structure.</p>
            </div>
          ) : viewMode === "chart" ? (
            <div className="overflow-x-auto py-6">
              <div className="flex flex-col items-center gap-0 min-w-fit">
                {roots.map(root => (
                  <div key={root.id} className="mb-8">
                    {renderChartNode(root, 0)}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-1" data-testid="org-tree-list">
              {roots.map(root => renderTreeItem(root, 0))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        <div className="flex items-center gap-1.5"><Crown className="h-3.5 w-3.5 text-amber-500" /> Head of Department</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded border-2 border-slate-200"></div> Employee</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded border-2 border-amber-300 bg-amber-50"></div> Department Head</div>
      </div>
    </div>
  );
};

export default OrgChart;
